"use client";

import { Cart } from "@/lib/shopify/types";
import { Dialog, Transition } from "@headlessui/react";
import Link from "next/link";
import Cookies from "js-cookie";
import { Fragment, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import PrimaryButton from "@/components/shared/PrimaryButton";
import { trackEvent } from "@/lib/amplitude";
import { computeBagTotals, computeLineDiscounts, isDigitalLine, lineAttr } from "@/lib/comix/bagConfig";
import { reconcileBagAgainstPurchases } from "@/lib/utils/comixBagActions";
import { BAG_UPDATED_EVENT } from "@/lib/comix/addToBag";
import { markCheckoutStarted } from "@/lib/comix/use-pending-approvals";
import { pushThemeColor } from "@/lib/theme-color";
import BagLineItem from "./BagLineItem";
import BagPromoBar from "./BagPromoBar";
import BagSummary from "./BagSummary";
import { siteConfig } from "@/lib/seo-config";
import WalletExpressPay from "@/components/comix/wallet/WalletExpressPay"; // wallet-express-pay

function buildCheckoutUrl(cart: Cart): string {
  const baseUrl = siteConfig.url;

  const runIds = cart.lines
    .filter((line) => lineAttr(line, "_productType") === "comix")
    .map((line) => lineAttr(line, "_runId"))
    .filter((id): id is string => Boolean(id))
    .map((id) => encodeURIComponent(id));
  const uniqueRunIds = Array.from(new Set(runIds));

  let returnTo: string | undefined;
  if (uniqueRunIds.length === 1) {
    returnTo = `${baseUrl}/comix/approve?runId=${uniqueRunIds[0]}`;
  } else if (uniqueRunIds.length > 1) {
    returnTo = `${baseUrl}/comix/purchase-complete?runIds=${uniqueRunIds.join(",")}`;
  }

  return returnTo
    ? `${cart.checkoutUrl}${cart.checkoutUrl.includes("?") ? "&" : "?"}return_to=${encodeURIComponent(returnTo)}`
    : cart.checkoutUrl;
}

export default function BagDrawer({ cart }: { cart: Cart | undefined }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [createHref, setCreateHref] = useState('/comix/create');
  const quantityRef = useRef(cart?.totalQuantity);

  // Mirrors the `cart` prop, but can also be updated directly from a line-item action's
  // return value (see onCartUpdate below) — quantity/removal changes are invoked as bare
  // action calls rather than through a tracked <form action>, so Next won't re-render this
  // already-mounted page on its own; router.refresh() was tried and rejected for this same
  // reason (it remounts the async server-side bag wrapper, closing the drawer with it).
  const [clientCart, setClientCart] = useState(cart);
  const [walletVisible, setWalletVisible] = useState(false); // wallet-express-pay
  const [walletLocked, setWalletLocked] = useState(false); // wallet-express-pay
  useEffect(() => {
    setClientCart(cart);
  }, [cart]);

  // Drop lines for comics the customer has already paid for (bought on another
  // device, via a different flow, or re-added after purchase). Runs on mount and
  // each time the bag opens, so a long-lived tab still catches purchases made
  // since it loaded. A completed checkout already clears the whole cart; this
  // covers what survives that.
  useEffect(() => {
    // Reconcile on mount, and again whenever the bag transitions to open (the
    // effect re-runs on every isOpen change; a close is a cheap no-op).
    let cancelled = false;
    reconcileBagAgainstPurchases()
      .then((next) => {
        if (!cancelled && next) setClientCart(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const totals = clientCart ? computeBagTotals(clientCart) : undefined;
  const lineDiscounts = clientCart ? computeLineDiscounts(clientCart, totals?.discountActive ? totals.totalSavings : 0) : {};
  const openBag = () => {
    trackEvent("Bag - Opened", { source: "icon", itemCount: clientCart?.totalQuantity ?? 0 });
    setIsOpen(true);
  };
  const closeBag = () => {
    // Guard so the many closeBag call sites (backdrop, Esc, X, Checkout, Create another)
    // don't emit a close event when the drawer is already shut.
    if (isOpen) trackEvent("Bag - Closed");
    setIsOpen(false);
  };

  useEffect(() => {
    const sessionId = Cookies.get('comixSessionId');
    if (!sessionId) return;
    fetch(`/api/comix/characters?sessionId=${sessionId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.characters?.length > 0) setCreateHref('/comix/stories');
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Open the bag when quantity changes (e.g. right after Add to Bag).
    if (cart?.totalQuantity !== quantityRef.current) {
      if (!isOpen && (cart?.totalQuantity ?? 0) > (quantityRef.current ?? 0)) {
        trackEvent("Bag - Opened", { source: "item-added", itemCount: cart?.totalQuantity ?? 0 });
        setIsOpen(true);
      }
      quantityRef.current = cart?.totalQuantity;
    }
  }, [isOpen, cart?.totalQuantity]);

  useEffect(() => {
    if (!isOpen) return;
    return pushThemeColor('#000000');
  }, [isOpen]);

  useEffect(() => {
    // Add to Bag on another page (unlock/checkout/stories) posts straight to a Route
    // Handler rather than going through a Server Action + revalidation — see
    // addToBag.ts and /api/cart/add for why: that path's cookie mutation would remount
    // this component via Next's automatic re-render, snapping the drawer shut right
    // after its own open effect fired. This event is how that page hands the fresh
    // cart to this already-mounted drawer instead.
    function onBagUpdated(e: Event) {
      const nextCart = (e as CustomEvent<Cart>).detail;
      if (!nextCart) return;
      trackEvent("Bag - Opened", { source: "item-added", itemCount: nextCart.totalQuantity ?? 0 });
      quantityRef.current = nextCart.totalQuantity;
      setClientCart(nextCart);
      setIsOpen(true);
    }
    window.addEventListener(BAG_UPDATED_EVENT, onBagUpdated);
    return () => window.removeEventListener(BAG_UPDATED_EVENT, onBagUpdated);
  }, []);

  return (
    // Single wrapper so Headless UI's portal placeholder mounts inside it —
    // as a direct child of the header's space-x-3 row it would pick up a
    // 12px margin and nudge the icons whenever the drawer opens.
    <div className="flex items-center">
      {/* Icon only shows once the bag has something in it. It stays mounted
          (visibility toggle) so it never pushes the neighboring header icons
          around when it appears or while the drawer opens. */}
      <button
        id="btn-bag-open"
        type="button"
        className="relative cursor-pointer text-xl text-dark dark:text-white"
        aria-hidden={false}
        tabIndex={0}
        aria-label="Open bag"
        onClick={openBag}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{ width: 20, height: 20 }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
        </svg>
        {clientCart && clientCart.totalQuantity > 0 ? (
          <div
            className="bg-gradient-to-r from-[#F52F81] to-[#FD7468] text-white rounded-full absolute flex items-center justify-center"
            style={{ fontWeight: 700, fontSize: 10, height: 18, width: 18, top: -5, right: -9 }}
          >
            {clientCart.totalQuantity}
          </div>
        ) : null}
      </button>

      <Transition show={isOpen}>
        <Dialog onClose={closeBag} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="fixed inset-y-0 right-0 flex h-full w-full flex-col bg-white text-[#333] md:w-[420px] md:border-l md:border-[#e0e0e0]">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-[#ececec] px-5 py-4">
                <Dialog.Title className="text-[18px] font-semibold">
                  Your Bag
                </Dialog.Title>
                <button
                  id="btn-bag-close"
                  type="button"
                  aria-label="Close bag"
                  onClick={closeBag}
                  className="p-1 text-[#828282] hover:text-[#333]"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>

              {!clientCart || clientCart.lines.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
                  {/* Bag icon */}
                  <div className="w-20 h-20 rounded-full bg-[#F5F5F7] flex items-center justify-center mb-6">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ADADB8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <path d="M16 10a4 4 0 01-8 0"/>
                    </svg>
                  </div>

                  <h3 className="text-[24px] font-semibold text-[#1D1D1F]" style={{ paddingBottom: '20px' }}>Your bag is empty</h3>

                  <PrimaryButton
                    id="link-bag-create-book"
                    href={createHref}
                    onClick={() => { trackEvent("Bag - Create Book Clicked", { state: "empty" }); closeBag(); }}
                    size="small"
                    className="rounded-2xl"
                    style={{ width: '140px' }}
                  >
                    Create a book
                  </PrimaryButton>
                </div>
              ) : (
                <>
                  {/* Line items */}
                  <ul className="flex-1 overflow-y-auto">
                    <AnimatePresence initial={false}>
                      {clientCart.lines.map((line) => (
                        <BagLineItem key={line.id} line={line} lineDiscount={lineDiscounts[line.id] ?? 0} onSyncChange={setIsSyncing} onCartUpdate={setClientCart} />
                      ))}
                    </AnimatePresence>
                  </ul>

                  {totals && <BagPromoBar totals={totals} />}
                  {totals && (
                    <BagSummary
                      totals={totals}
                      isSyncing={isSyncing}
                      checkoutUrl={buildCheckoutUrl(clientCart)}
                      onCheckout={() => {
                        trackEvent("Bag - Checkout Clicked", {
                          totalAmount: totals.total,
                          itemCount: clientCart.totalQuantity,
                          items: clientCart.lines.map((line) => ({
                            runId: lineAttr(line, "_runId"),
                            title: lineAttr(line, "_title"),
                            product: lineAttr(line, "_product"),
                            quantity: line.quantity,
                          })),
                        });
                        // The pending-approval banner covers printed AND digital books now —
                        // retry it until the order-paid webhook lands either way.
                        markCheckoutStarted();
                      }}
                      onCreateAnother={closeBag}
                      // wallet-express-pay: pay for the whole bag with Apple/Google Pay
                      walletVisible={walletVisible}
                      walletLocked={walletLocked}
                      walletSlot={
                        <WalletExpressPay
                          runId={null}
                          buttonClassName="mt-4"
                          sessionId={clientCart.lines.map((l) => lineAttr(l, "_sessionId")).find(Boolean) ?? null}
                          product={clientCart.lines.some((l) => !isDigitalLine(l)) ? "printed" : "digital"}
                          productLabel={`Your bag (${clientCart.totalQuantity} ${clientCart.totalQuantity === 1 ? "item" : "items"})`}
                          bag={{
                            hasPrinted: clientCart.lines.some((l) => !isDigitalLine(l)),
                            signature: `${clientCart.lines.map((l) => `${l.id}:${l.quantity}`).join("|")}#${clientCart.cost.totalAmount.amount}`,
                          }}
                          onLockedChange={setWalletLocked}
                          onVisibleChange={setWalletVisible}
                        />
                      }
                    />
                  )}
                </>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </div>
  );
}
